import React from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import TypedText from '~/shared/components/TypedText';
import { Link } from '~/techstyle-shared/next-routes';
import { AssetContainer } from '~/techstyle-shared/react-assets';
import {
  Button,
  useBreakpoint,
  mobile,
  desktop,
} from '~/techstyle-shared/react-components';

const Wrapper = styled.figure`
  isolation: isolate;
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 24px 1fr;
  grid-template-areas: 'asset' 'overlap' 'description';
  grid-gap: 0;
  width: min(100%, 400px);
  color: ${({ theme }) => theme.colors.textDefault};
  text-align: center;
  box-shadow: 0px 0px 28px -5px rgba(0, 0, 0, 0.15);
  border-radius: 16px;
  margin: 0;
  ${desktop`
    width: min(calc((100vw / 3) - 56px), 400px);
  `}
`;

const FabricAsset = styled.div`
  grid-area: asset-start / asset-start / overlap-end / asset-end;
  margin: 0;
`;

const FabricDescription = styled.figcaption`
  z-index: 2;
  grid-area: overlap-start / description-start / description-end /
    description-end;
  justify-self: stretch;
  min-height: 170px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 32px 20px;
  background: ${({ theme }) => theme.colors.palette.white};
  border-radius: 16px;
  ${mobile`
    padding: 32px 18px;
  `}
`;

const AssetPlaceholder = styled.div`
  background: ${({ theme }) => theme.colors.fabricCardAssetPlaceholder};
  overflow: hidden;
  border-radius: 16px;
  height: ${({ $assetHeight }) => $assetHeight};
`;

const Title = styled(TypedText).attrs({
  type: 'header',
  forwardedAs: 'h2',
  size: 20,
})`
  text-transform: uppercase;
  margin: 4px 0px;
`;

const Description = styled(TypedText).attrs({
  type: 'body',
  forwardedAs: 'div',
  size: 12,
})``;

const Paragraph = styled(TypedText).attrs({
  type: 'body',
  forwardedAs: 'p',
  size: 14,
})`
  margin: 8px 0px;
`;

const StyledImageAsset = styled(AssetContainer.ImageAsset)`
  width: 100%;
  height: auto;
  border-radius: 8px;
  picture img {
    border-radius: 8px;
  }
`;

const Actions = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  margin-top: 8px;
`;

const StyledButton = styled(Button)`
  height: auto;
  width: auto;
  padding: 10px;
  border-radius: 8px;
  font-size: 16px;
  text-align: center;
  border-width: 3px;
`;

const ComingSoon = styled(AssetContainer.ImageAsset)`
  overflow: hidden;
  width: 295px;
  height: 397px;
  border-radius: 16px;
`;

const ComingSoonPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: absolute;
  width: 295px;
  height: 397px;
  left: 0px;
  top: 0px;
  background: linear-gradient(
    158.78deg,
    rgba(128, 49, 167, 0.8) 13.86%,
    rgba(255, 134, 116, 0.8) 83.73%
  );
  color: ${({ theme }) => theme.colors.textLight};
  border-radius: 16px;
  padding: 0 20px;
`;

export default function FabricCard({
  assetHeight = '194px',
  fabric,
  fabricKey,
  handleLearnMore,
  comingSoon = false,
  className,
}) {
  const { isMobile } = useBreakpoint();

  return (
    <Wrapper className={className}>
      {comingSoon ? (
        <FabricAsset>
          <AssetContainer
            name={isMobile ? fabric.asset.mobile : fabric.asset.desktop}
          >
            {({ data }) =>
              data ? <ComingSoon asset={data} /> : <ComingSoonPlaceholder />
            }
          </AssetContainer>
        </FabricAsset>
      ) : (
        <>
          <FabricAsset>
            <AssetContainer
              name={isMobile ? fabric.asset.mobile : fabric.asset.desktop}
            >
              {({ data }) =>
                data ? (
                  <StyledImageAsset asset={data} $assetHeight={assetHeight} />
                ) : (
                  <AssetPlaceholder $assetHeight={assetHeight} />
                )
              }
            </AssetContainer>
          </FabricAsset>
          <FabricDescription>
            <Title>{fabric.header.message}</Title>
            {fabric.materials && (
              <Description>{fabric.materials.message}</Description>
            )}
            <Paragraph>{fabric.paragraph.message}</Paragraph>
            <Actions>
              {fabric.buttons.learnMore && (
                <StyledButton
                  variant={fabric.buttons.learnMore.variant}
                  onClick={handleLearnMore}
                  data-testid={`learn-more-${fabricKey}`}
                >
                  {fabric.buttons.learnMore.message}
                </StyledButton>
              )}
              <Link href={fabric.buttons.shop.path}>
                <StyledButton
                  variant={fabric.buttons.shop.variant}
                  data-autotag={fabric.buttons.shop['data-autotag']}
                >
                  {fabric.buttons.shop.message}
                </StyledButton>
              </Link>
            </Actions>
          </FabricDescription>
        </>
      )}
    </Wrapper>
  );
}

const TranslationType = PropTypes.shape({
  message: PropTypes.node.isRequired,
});

FabricCard.propTypes = {
  assetHeight: PropTypes.string,
  className: PropTypes.string,
  comingSoon: PropTypes.bool,
  fabric: PropTypes.shape({
    asset: PropTypes.shape({
      mobile: PropTypes.string.isRequired,
      desktop: PropTypes.string.isRequired,
    }),
    header: TranslationType.isRequired,
    materials: TranslationType,
    paragraph: TranslationType.isRequired,
    buttons: PropTypes.shape({
      learnMore: PropTypes.shape({
        message: PropTypes.node.isRequired,
        variant: PropTypes.string.isRequired,
      }),
      shop: PropTypes.shape({
        message: PropTypes.node.isRequired,
        path: PropTypes.string.isRequired,
        variant: PropTypes.string.isRequired,
        'data-autotag': PropTypes.string.isRequired,
      }),
    }),
  }).isRequired,
  fabricKey: PropTypes.string.isRequired,
  handleLearnMore: PropTypes.func,
};
